import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { CircularProgress, FormHelperText, Grid } from "@mui/material";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import axios from "axios";
import dayjs from "dayjs";
import { useFormik } from "formik";
import * as yup from "yup";

import CommonButton from "../../common/button/Button";
import CommonDropDown from "../../common/dropdown/CommonDropdown";
import CommonInput from "../../common/input/InputBox";
import UploadImage from "../../common/uploadImage/UploadImage";
import { DatePicker } from "@mui/x-date-pickers";

interface BasicShopDetails {
  fullName: string;
  address: string;
  logo: string;
  subscriptionPlan: string;
  email: string;
  phone: string;
  paymentStatus: string;
  storename: string;
  ticketslocation: string;
  nextSubscriptionDate: any;
}

interface ShopFormProps {
  data: BasicShopDetails;
  onFormSubmit: (values: any) => void;
  loading: boolean;
  logo?: any;
  setLogo?: any;
  edit?: boolean;
}

const ShopForm: React.FC<ShopFormProps> = ({
  loading,
  data,
  onFormSubmit,
  logo,
  setLogo,
  edit = false,
}) => {
  //@ts-ignore
  const config = window.config;
  const apiBaseURL = config?.server || "http://18.217.95.31:8080";
  const phoneNumberRegex = config.phoneRegExp;
  const { t } = useTranslation("translation");
  const [imageLoading, setImageLoading] = useState(false);
  const [subscriptionOptionsLoading, setSubscriptionOptionsLoading] = useState(false);
  const [subscriptionOptions, setSubscriptionOptions] = useState<any>([]);
  const [nextSubscriptionDate, setNextSubscriptionDate] = useState<any>(
    dayjs(data.nextSubscriptionDate),
  );
  const validationSchema = yup.object().shape({
    fullName: yup.string().required(t("fullnameRequired")),
    address: yup.string().required(t("addressRequired")),
    logo: yup.mixed().required(t("logoRequired")),
    subscriptionPlan: yup
      .string()
      .trim()
      .required(t("subscriptionPlanRequired", "Subscription plan is required")),
    email: yup.string().email(t("invalidEmail")).required(t("emailRequired")),
    phone: yup
      .string()
      .required(t("phoneRequired"))
      .matches(phoneNumberRegex, t("phoneNumberErrorText", "Phone number is not valid")),
    paymentStatus: yup.string().trim().required(t("paymentRequired")),
    storename: yup.string().trim().required(t("storeNameRequired")),
    ticketslocation: yup.string().trim().required(t("ticketsLocationRequired")),
    nextSubscriptionDate: yup.string().required(t("nextSubscriptionDateRequired")),
  });

  const handleImageUpload = (event: any) => {
    const file = URL.createObjectURL(event?.target?.files[0]);
    setLogo(file);
    const fileFound = event.target.type === "file" && event.target.files[0];
    setFieldValue("logo", fileFound);
  };

  const formik = useFormik({
    initialValues: data,
    onSubmit: onFormSubmit,
    validationSchema: validationSchema,
    enableReinitialize: true,
  });

  useEffect(() => {
    if (values?.logo) {
      setLogo(values?.logo);
    }
  }, []);

  useEffect(() => {
    axios
      .get(`${apiBaseURL}/api/subscription/fetchSubscriptions`)
      .then((data: any) => {
        setSubscriptionOptionsLoading(true);
        console.log("data", data);
        const options: any = data?.data?.subscriptions.map((i: any) => ({
          key: i.customSubscriptionName,
          value: i.customSubscriptionId,
        }));
        setSubscriptionOptions([...[{ value: " ", key: "Select Plan" }], ...options]);
      })
      .catch((err: any) => {
        console.log("err", err);
      })
      .finally(() => {
        setSubscriptionOptionsLoading(false);
      });
  }, []);

  const {
    values,
    errors,
    handleSubmit,
    touched,
    handleChange,
    setFieldValue,
    resetForm,
    handleReset,
  } = formik;

  const onLoad = () => {
    setImageLoading(false);
  };
  // const subscriptionOptions =[
  //   {value:" ", key: "Select Plan"},
  //   { value: "1", key: "Subscription 1" },
  //   { value: "2", key: "Subscription 2" },
  //   { value: "3", key: "Subscription 3" },
  //   { value: "4", key: "Subscription 4" },
  // ];

  const paymentStatusOptions = [
    { value: " ", key: "Select Status" },
    { value: "PENDING", key: "Pending" },
    { value: "PAID", key: "Paid" },
  ];

  const ticketsLocationOptions = [
    { value: " ", key: "Select Location" },
    { value: "TEXAS", key: "Texas" },
  ];

  return (
    <Grid className='h-screen bg-white p-[2%]'>
      <Grid className='text-[1.25rem] font-[500]'>{t("shopDetails")}</Grid>
      <Grid className='grid grid-cols-2 mt-1 gap-12'>
        <Grid className='col-span-1'>
          <Grid>
            <CommonInput
              id='title-id-input-box'
              fullWidth
              name='fullName'
              label={t("fullname")}
              placeholder={t("enterfullName")}
              onChange={handleChange}
              value={values?.fullName}
              className='input-box'
              sx={true}
              error={touched?.fullName && errors?.fullName ? true : false}
              helperText={errors?.fullName && touched?.fullName ? errors?.fullName : ""}
            />
          </Grid>
          <Grid>
            <CommonInput
              label={t("email")}
              placeholder={t("enterEmail")}
              fullWidth
              name='email'
              onChange={handleChange}
              sx={true}
              value={values?.email}
              error={touched?.email && errors?.email ? true : false}
              helperText={errors?.email && touched?.email ? errors?.email : ""}
              className='input-box'
            />
          </Grid>
          <Grid className='relative'>
            <Grid className='text-[0.75rem]'>
              <Grid
                className={`${touched?.logo && errors?.logo ? "text-red-700" : "text-[#00000099]"}`}
              >
                {t("shopLogo")}
              </Grid>
            </Grid>
            <UploadImage
              name='logo'
              onChange={(event: any) => handleImageUpload(event)}
              accept='.png,.jpg,.jpeg'
            />
            {!logo ? (
              <Grid>
                <Grid className='bg-[#f1f0ee] mt-1 w-[50%] text-[#00000099] text-[0.8rem] h-[80px] flex justify-center items-center rounded-[8px]'>
                  {t("uploadShopLogo")}
                </Grid>
                <FormHelperText
                  error={touched?.logo && errors?.logo ? true : false}
                  className='input-helper-text'
                >
                  {errors?.logo && touched?.logo ? errors?.logo : ""}
                </FormHelperText>
              </Grid>
            ) : (
              <Grid></Grid>
            )}
            {!errors?.logo ? (
              <Grid item xs={6} className='h-[110px]'>
                {logo && (
                  <img
                    alt='lottery'
                    src={logo}
                    onLoad={onLoad}
                    className='w-auto h-full aspect-square object-cover rounded-[20%] mt-2'
                  />
                )}
              </Grid>
            ) : (
              <Grid item xs={6} className='h-[110px]'></Grid>
            )}
            {imageLoading && (
              <CircularProgress
                color='inherit'
                size={14}
                thickness={6}
                style={{ marginLeft: "10%" }}
              />
            )}
          </Grid>
          <Grid>
            <CommonDropDown
              id='payment-status-id-dropdown-box'
              fullWidth
              name='paymentStatus'
              label={t("paymentStatus")}
              options={paymentStatusOptions}
              className='mb-8 mt-8'
              sx={{ width: "100%", height: "15%", padding: "2px" }}
              onChange={handleChange}
              value={values?.paymentStatus}
              error={touched?.paymentStatus && errors?.paymentStatus ? true : false}
              helperText={
                errors?.paymentStatus && touched?.paymentStatus ? errors?.paymentStatus : ""
              }
            />
          </Grid>

          {values?.paymentStatus === "PAID" && (
            <Grid>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={["DatePicker"]}>
                  <DatePicker
                    className='date-field'
                    name='nextSubscriptionDate'
                    label={t("nextSubscriptionDate")}
                    onChange={(newValue) => {
                      setNextSubscriptionDate(newValue);
                      setFieldValue("nextSubscriptionDate", newValue.format("MM-DD-YYYY"));
                    }}
                    value={nextSubscriptionDate}
                    disablePast
                    slotProps={{
                      textField: {
                        error: touched?.nextSubscriptionDate && errors?.nextSubscriptionDate ? true : false,
                        helperText:
                          errors?.nextSubscriptionDate && touched?.nextSubscriptionDate
                            ? (errors?.nextSubscriptionDate as string)
                            : "",
                        variant: "standard",
                      },
                    }}
                    sx={{ width: "50%" }}
                  />
                </DemoContainer>
              </LocalizationProvider>
            </Grid>
          )}
          <Grid>
            <CommonDropDown
              id='ticket-location-id-dropdown-box'
              fullWidth
              name='ticketslocation'
              label={t("ticketsLocation")}
              options={ticketsLocationOptions}
              className='mb-8 mt-8'
              sx={{ width: "100%", height: "15%", padding: "2px" }}
              onChange={handleChange}
              value={values?.ticketslocation}
              error={touched?.ticketslocation && errors?.ticketslocation ? true : false}
              helperText={
                errors?.ticketslocation && touched?.ticketslocation ? errors?.ticketslocation : ""
              }
            />
          </Grid>
        </Grid>
        <Grid className='col-span-1 mt-4'>
          <Grid>
            {subscriptionOptionsLoading && (
              <CircularProgress
                color='inherit'
                size={14}
                thickness={6}
                style={{ marginLeft: "10%" }}
              />
            )}
            <CommonDropDown
              id='subscription-id-dropdown-box'
              fullWidth
              name='subscriptionPlan'
              label={t("subscriptionPlan")}
              options={subscriptionOptions}
              className='mb-8'
              sx={{ width: "100%", height: "15%", padding: "2px" }}
              onChange={handleChange}
              value={values?.subscriptionPlan}
              error={touched?.subscriptionPlan && errors?.subscriptionPlan ? true : false}
              helperText={
                errors?.subscriptionPlan && touched?.subscriptionPlan
                  ? errors?.subscriptionPlan
                  : ""
              }
            />
          </Grid>
          <Grid>
            <CommonInput
              label={t("phone")}
              placeholder={t("enterPhone")}
              fullWidth
              name='phone'
              onChange={handleChange}
              sx={true}
              className='input-box'
              value={values?.phone}
              error={touched?.phone && errors?.phone ? true : false}
              helperText={errors?.phone && touched?.phone ? errors?.phone : ""}
            />
          </Grid>
          <Grid>
            <CommonInput
              id='address-id-input-box'
              minRows={7}
              maxRows={7}
              fullWidth
              multiline
              name='address'
              label={t("address")}
              placeholder={t("enterAddress")}
              onChange={handleChange}
              value={values?.address}
              sx={true}
              error={touched?.address && errors?.address ? true : false}
              helperText={errors?.address && touched?.address ? errors?.address : ""}
              className='input-box'
            />
          </Grid>
          <Grid>
            <CommonInput
              id='storename-id-input-box'
              fullWidth
              name='storename'
              label={t("storeName", "Store Name")}
              placeholder={t("enterStoreName")}
              onChange={handleChange}
              value={values?.storename}
              sx={true}
              error={touched?.storename && errors?.storename ? true : false}
              helperText={errors?.storename && touched?.storename ? errors?.storename : ""}
              className='input-box'
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid className='mt-2 h-[10%] flex justify-start gap-4'>
        <Grid className='w-[15%]'>
          <CommonButton
            id='submit-button'
            className='login-button'
            variant='contained'
            size='small'
            disabled={loading || (edit && JSON.stringify(values) === JSON.stringify(data))}
            onClick={() => {
              handleSubmit();
            }}
          >
            {edit ? t("updateButton", "Update") : t("save")}
            {loading && (
              <CircularProgress
                color='inherit'
                size={14}
                thickness={6}
                style={{ marginLeft: "8px" }}
              />
            )}
          </CommonButton>
        </Grid>
        <Grid className='w-[15%]'>
          <CommonButton
            id='reset-button'
            className='sign-up-button'
            variant='outlined'
            size='small'
            onClick={(e) => {
              setLogo(data.logo);
              handleReset(e);
            }}
          >
            {t("reset")}
          </CommonButton>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default ShopForm;
